import type { Occurrence } from '@klopotun/contracts';
import { addMonths, clampDay, formatCivilDate, type CivilDate } from '@klopotun/domain';

const MONTHS_OF_UPCOMING = 1;

const isPlanned = (occurrence: Occurrence): boolean => occurrence.status === 'planned';

/**
 * The nearest period still waiting to be paid, per payment. An overdue one wins over a future one
 * on purpose: it is the next thing actually owed, and skipping it would leave a listing quoting a
 * date that is not the one to act on.
 */
export const earliestDueDateByPayment = (occurrences: Occurrence[]): Map<string, string> =>
  occurrences.filter(isPlanned).reduce((earliest, occurrence) => {
    const known = earliest.get(occurrence.paymentId);
    const isEarlier = known === undefined || occurrence.dueDate < known;

    return isEarlier ? earliest.set(occurrence.paymentId, occurrence.dueDate) : earliest;
  }, new Map<string, string>());

/**
 * What "upcoming" is allowed to mean on the main screen: everything already owed, plus what falls
 * due within the coming month. The materialiser fills rows to the end of the calendar year and
 * never less than three months out (design note F14), so an unbounded listing opens on a year of
 * them - some ninety rows on fifteen payments - under a heading that promises the nearest ones.
 *
 * Stored dates are ISO, so comparing them as text is the same as comparing them as dates.
 */
export const dueWithinUpcoming = (occurrences: Occurrence[], today: CivilDate): Occurrence[] => {
  const lastDay = formatCivilDate(clampDay(addMonths(today, MONTHS_OF_UPCOMING)));

  return occurrences.filter((occurrence) => occurrence.dueDate <= lastDay);
};
